/**
 * Optimized API client with caching, debouncing, and performance monitoring
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { errorHandler } from './errorHandler';

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

// Cache configuration
interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum number of cached items
}

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class ApiCache {
  private cache = new Map<string, CacheItem<any>>();
  private config: CacheConfig;

  constructor(config: CacheConfig = { ttl: 5 * 60 * 1000, maxSize: 100 }) {
    this.config = config;
  }

  set<T>(key: string, data: T, ttl?: number): void {
    // Remove oldest items if cache is full
    if (this.cache.size >= this.config.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.ttl
    });
  }

  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // Check if item has expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

// Debounce utility
class Debouncer {
  private timers = new Map<string, NodeJS.Timeout>();

  debounce<T extends (...args: any[]) => any>(
    key: string,
    fn: T,
    delay: number
  ): T {
    return ((...args: Parameters<T>) => {
      const existingTimer = this.timers.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        fn(...args);
        this.timers.delete(key);
      }, delay);

      this.timers.set(key, timer);
    }) as T;
  }

  cancel(key: string): void {
    const timer = this.timers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(key);
    }
  }

  cancelAll(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
  }
}

// Performance monitoring
class PerformanceMonitor {
  private metrics = new Map<string, {
    count: number;
    totalTime: number;
    minTime: number;
    maxTime: number;
    errors: number;
  }>();

  record(operation: string, duration: number, success: boolean): void {
    const existing = this.metrics.get(operation) || {
      count: 0,
      totalTime: 0,
      minTime: Infinity,
      maxTime: 0,
      errors: 0
    };

    existing.count++;
    existing.totalTime += duration;
    existing.minTime = Math.min(existing.minTime, duration);
    existing.maxTime = Math.max(existing.maxTime, duration);

    if (!success) {
      existing.errors++;
    }

    this.metrics.set(operation, existing);
  }

  getMetrics(operation?: string) {
    if (operation) {
      return this.metrics.get(operation);
    }
    return Object.fromEntries(this.metrics);
  }

  clear(): void {
    this.metrics.clear();
  }
}

// Optimized API client
class OptimizedApiClient {
  private axiosInstance: AxiosInstance;
  private cache: ApiCache;
  private debouncer: Debouncer;
  private performanceMonitor: PerformanceMonitor;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 120000, // 2 minutes - UPS API calls are synchronous
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.cache = new ApiCache({
      ttl: 5 * 60 * 1000, // 5 minutes
      maxSize: 100
    });

    this.debouncer = new Debouncer();
    this.performanceMonitor = new PerformanceMonitor();

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add request timestamp for performance monitoring
        config.metadata = { startTime: Date.now() };
        
        // Add authentication token
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Token ${token}`;
        }
        
        return config;
      },
      (error) => {
        errorHandler.handleApiError(error, 'Request Interceptor');
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        const duration = Date.now() - (response.config.metadata?.startTime || Date.now());
        this.performanceMonitor.record(
          `${response.config.method?.toUpperCase()} ${response.config.url}`,
          duration,
          true
        );
        return response;
      },
      (error) => {
        const duration = Date.now() - (error.config?.metadata?.startTime || Date.now());
        this.performanceMonitor.record(
          `${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          duration,
          false
        );
        
        // Handle 401 Unauthorized - redirect to login
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        
        errorHandler.handleApiError(error, 'Response Interceptor');
        return Promise.reject(error);
      }
    );
  }

  // GET request (caching handled by React Query)
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.get<T>(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Debounced GET request
  debouncedGet<T>(
    url: string,
    config?: AxiosRequestConfig,
    delay: number = 300
  ): Promise<T> {
    const cacheKey = `GET:${url}:${JSON.stringify(config?.params || {})}`;
    
    return new Promise((resolve, reject) => {
      const debouncedRequest = this.debouncer.debounce(
        cacheKey,
        async () => {
          try {
            const result = await this.get<T>(url, config);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        delay
      );

      debouncedRequest();
    });
  }

  // POST request with caching control
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    
    // Invalidate related cache entries
    this.invalidateCache(url);
    
    return response.data;
  }

  // PUT request
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    
    // Invalidate related cache entries
    this.invalidateCache(url);
    
    return response.data;
  }

  // DELETE request
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    
    // Invalidate related cache entries
    this.invalidateCache(url);
    
    return response.data;
  }

  // Batch requests
  async batch<T>(requests: Array<() => Promise<T>>): Promise<T[]> {
    const startTime = Date.now();
    
    try {
      const results = await Promise.all(requests.map(request => request()));
      
      const duration = Date.now() - startTime;
      this.performanceMonitor.record('BATCH_REQUEST', duration, true);
      
      return results;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.performanceMonitor.record('BATCH_REQUEST', duration, false);
      throw error;
    }
  }

  // Cache management
  invalidateCache(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    // Simple pattern matching for cache invalidation
    const cacheMap = this.cache as any;
    const keys = Array.from(cacheMap.cache.keys()) as string[];
    for (const key of keys) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  // Performance metrics
  getPerformanceMetrics(operation?: string) {
    return this.performanceMonitor.getMetrics(operation);
  }

  // Cache statistics
  getCacheStats() {
    return {
      size: this.cache.size(),
      maxSize: this.cache['config'].maxSize,
      ttl: this.cache['config'].ttl
    };
  }

  // Clear all data
  clear(): void {
    this.cache.clear();
    this.debouncer.cancelAll();
    this.performanceMonitor.clear();
  }
}

// Create optimized API instance
const optimizedApi = new OptimizedApiClient('https://shipx.gxpresss.com/api');

// Service-specific optimized methods
export const optimizedApiService = {
  // Shipments API with caching
  shipments: {
    getShipments: (params?: any) => optimizedApi.get('/labels/shipments/', { params }),
    getShipment: (id: number) => optimizedApi.get(`/labels/shipments/${id}/`),
    createShipment: (shipmentData: any) => optimizedApi.post('/labels/shipments/', shipmentData),
    updateShipment: (id: number, shipmentData: any) => optimizedApi.put(`/labels/shipments/${id}/`, shipmentData),
    deleteShipment: (id: number) => optimizedApi.delete(`/labels/shipments/${id}/`),
    processUpsShipment: (id: number) => optimizedApi.post(`/labels/shipments/${id}/process_ups_shipment/`),
    calculateRates: (shipmentData: any) => optimizedApi.post('/labels/shipments/calculate_rates/', shipmentData),
    downloadShipmentPdf: (id: number) => optimizedApi.get(`/labels/shipments/${id}/download_shipment_pdf/`, {
      responseType: 'blob'
    }),
    downloadLabelPdf: (id: number, packageIndex: number) => optimizedApi.get(`/labels/shipments/${id}/download_label_pdf/`, {
      params: { package_index: packageIndex },
      responseType: 'blob'
    }),
    shipShipment: (id: number) => optimizedApi.post(`/labels/shipments/${id}/ship_shipment/`),
    exportShipments: (dateFrom?: string, dateTo?: string) => optimizedApi.get('/labels/shipments/export_shipments/', {
      params: { date_from: dateFrom, date_to: dateTo },
      responseType: 'blob'
    }),
    adminExportShipments: (dateFrom?: string, dateTo?: string, userId?: number) => optimizedApi.get('/labels/shipments/admin_export_shipments/', {
      params: { date_from: dateFrom, date_to: dateTo, user_id: userId },
      responseType: 'blob'
    }),
  },

  // Labels API with caching (legacy support)
  labels: {
    getLabels: (params?: any) => optimizedApi.get('/labels/labels/', { params }),
    getLabel: (id: number) => optimizedApi.get(`/labels/labels/${id}/`),
    createLabel: (labelData: any) => optimizedApi.post('/labels/labels/', labelData),
    updateLabel: (id: number, labelData: any) => optimizedApi.put(`/labels/labels/${id}/`, labelData),
    deleteLabel: (id: number) => optimizedApi.delete(`/labels/labels/${id}/`),
    printLabel: (id: number) => optimizedApi.post(`/labels/labels/${id}/print_label/`, {}, {
      responseType: 'blob'
    }),
    downloadPackageLabel: (id: number, packageIndex: number) => optimizedApi.get(`/labels/labels/${id}/download_package_label/`, {
      params: { package_index: packageIndex },
      responseType: 'blob'
    }),
    shipLabel: (id: number) => optimizedApi.post(`/labels/labels/${id}/ship_label/`),
    processUpsLabel: (id: number) => optimizedApi.post(`/labels/labels/${id}/process_ups_label/`),
    bulkDownload: (labelIds: number[]) => optimizedApi.get('/labels/labels/bulk_download/', { 
      params: { ids: labelIds },
      responseType: 'blob'
    }),
  },

  // Bulk Jobs API
  bulkJobs: {
    previewBulkFile: (file: File, serviceType: string) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('service_type', serviceType);
      return optimizedApi.post('/labels/bulk-jobs/preview_file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minutes - CSV preview with rate calculation can take time
      });
    },
    uploadBulkFile: (file: File, serviceType: string) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('service_type', serviceType);
      return optimizedApi.post('/labels/bulk-jobs/upload_file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getBulkJobs: (params?: any) => optimizedApi.get('/labels/bulk-jobs/', { params }),
    getBulkJob: (id: number) => optimizedApi.get(`/labels/bulk-jobs/${id}/`),
    downloadBulkResults: (id: number) => optimizedApi.get(`/labels/bulk-jobs/${id}/download_results/`, {
      responseType: 'blob'
    }),
    downloadAllLabels: (id: number) => optimizedApi.get(`/labels/bulk-jobs/${id}/download-all-labels/`, {
      responseType: 'blob'
    }),
  },

  // Address validation with debouncing
  addresses: {
    validateAddress: (addressData: any) => optimizedApi.post('/addresses/validate/', addressData),
  },

  // Tracking with caching
  tracking: {
    trackShipment: (trackingNumber: string) => optimizedApi.get(`/tracking/track/${trackingNumber}/`),
  },

  // Auth API
  auth: {
    login: (credentials: any) => optimizedApi.post('/auth/login/', credentials),
    logout: () => optimizedApi.post('/auth/logout/'),
    refreshToken: () => optimizedApi.post('/auth/refresh/'),
  },

  // Health and status
  health: {
    check: () => optimizedApi.get('/health/'),
    servicesStatus: () => optimizedApi.get('/services/status/'),
  },

  // Cache management
  cache: {
    invalidate: (pattern?: string) => optimizedApi.invalidateCache(pattern),
    clear: () => optimizedApi.clear(),
    stats: () => optimizedApi.getCacheStats(),
  },

  // Performance monitoring
  performance: {
    metrics: (operation?: string) => optimizedApi.getPerformanceMetrics(operation),
  }
};

export default optimizedApiService;
